// Cada acción del asistente queda en una línea (ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea).
//
// Cada ronda del agente abre un mensaje propio. Las rondas que sólo usaron herramientas sin card
// (buscar, mirar los productos, leer una planilla) dejaban un mensaje VACÍO que igual ocupaba su lugar
// en la lista: cada acción bajaba un renglón y la mascota saltaba, sin que se viera qué había hecho.
// Ahora esas rondas se juntan en un bloque de líneas —"Buscó en tus productos · 3 veces"— y los
// mensajes vacíos no se dibujan. La mascota, que va al pie de la lista, queda debajo de la última.
//
// Puro: el Chat y el Asistente de Crear dibujan las mismas filas.

import { accionHecha, type TipoDeActividad } from './actividadDelAgente';

export interface LineaDeAccion {
  herramienta: string;
  texto: string;
  tipo: TipoDeActividad;
  /** Cuántas veces seguidas la hizo (se dibuja "· 3 veces"). */
  veces: number;
}

/** Las acciones (nombres de herramientas, en orden) como líneas. Las que no dejan línea se saltean. */
export function lineasDeAcciones(acciones: readonly string[]): LineaDeAccion[] {
  const out: LineaDeAccion[] = [];
  for (const herramienta of acciones) {
    const hecha = accionHecha(herramienta);
    if (!hecha) continue;
    const ultima = out[out.length - 1];
    if (ultima && ultima.herramienta === herramienta) ultima.veces += 1;
    else out.push({ herramienta, texto: hecha.texto, tipo: hecha.tipo, veces: 1 });
  }
  return out;
}

/** Lo que mira la agrupación de un mensaje (el resto no importa acá). */
export interface MensajeParaFilas {
  role: 'user' | 'assistant';
  text?: string | null;
  kind?: string;
  toolCards?: readonly unknown[];
  acciones?: readonly string[];
  graficos?: readonly unknown[];
  sugerencias?: readonly unknown[];
  desbloquear?: unknown;
  verCalendario?: boolean;
}

export type FilaDeConversacion =
  | { tipo: 'mensaje'; indice: number }
  /** `clave`: la del primer mensaje del bloque; no cambia mientras el bloque crece. */
  | { tipo: 'acciones'; clave: string; lineas: LineaDeAccion[] };

/** ¿El mensaje no dibuja nada propio? (ni texto, ni card, ni gráfico, ni sugerencias…). */
function sinNadaPropio(m: MensajeParaFilas): boolean {
  return (
    m.role === 'assistant' &&
    m.kind !== 'upgrade' &&
    !(m.text ?? '').trim() &&
    !(m.toolCards?.length) &&
    !(m.graficos?.length) &&
    !(m.sugerencias?.length) &&
    !m.desbloquear &&
    m.verCalendario !== true
  );
}

/**
 * Las filas de la conversación: cada mensaje como siempre, salvo los del asistente que no dibujan
 * nada propio, que pasan a líneas de acción (juntas con las del mensaje anterior del mismo turno) o
 * desaparecen si tampoco hicieron nada que deje línea.
 */
export function filasDeLaConversacion(mensajes: readonly MensajeParaFilas[]): FilaDeConversacion[] {
  const filas: FilaDeConversacion[] = [];
  /** El bloque abierto, al que se suman las acciones de las rondas que siguen. */
  let bloque: { clave: string; acciones: string[] } | null = null;
  const cerrar = () => {
    if (!bloque) return;
    const lineas = lineasDeAcciones(bloque.acciones);
    if (lineas.length > 0) filas.push({ tipo: 'acciones', clave: bloque.clave, lineas });
    bloque = null;
  };
  mensajes.forEach((m, indice) => {
    if (sinNadaPropio(m)) {
      if (!m.acciones?.length) return;
      if (!bloque) bloque = { clave: `acciones-${indice}`, acciones: [] };
      bloque.acciones.push(...m.acciones);
      return;
    }
    cerrar();
    filas.push({ tipo: 'mensaje', indice });
    if (m.role === 'assistant' && m.acciones?.length) bloque = { clave: `acciones-${indice}`, acciones: [...m.acciones] };
  });
  cerrar();
  return filas;
}
